import { useLayoutEffect } from 'react';

// Liquid-glass selection lens.
//
// The container gets the `glass-track` class (styles/global.css). This hook
// finds its selected child and writes that child's box into CSS custom
// properties; the track's ::before is the lens, so moving between items is a
// single element sliding on a spring — never two backgrounds cross-fading.
// While it travels it stretches along the direction of motion and settles
// back, which is what reads as "morphing" rather than jumping.
//
// `deps` should change whenever the selection can change (route, tab state).
const SELECTED = '[aria-current="page"], [aria-selected="true"], [aria-pressed="true"], [aria-checked="true"]';

export default function useGlassIndicator(ref, deps = [], selector = SELECTED) {
  useLayoutEffect(() => {
    const track = ref.current;
    if (!track) return undefined;
    let settle;

    const place = (animate) => {
      const el = track.querySelector(selector);
      if (!el || el.offsetParent === null) {
        track.style.setProperty('--glass-o', '0');
        return;
      }
      // Layout offsets, not client rects: they ignore transforms, so a track
      // that is itself animating (a menu scaling open) still measures true.
      let x;
      let y;
      let w = el.offsetWidth;
      let h = el.offsetHeight;
      // In RTL a scrolling track puts its scrollbar on the left. The lens's
      // `left: 0` starts after that scrollbar but offsetLeft counts from before
      // it, so the lens landed a scrollbar-width off. Rects measure it right.
      const leftScrollbar = track.clientLeft - (parseFloat(getComputedStyle(track).borderLeftWidth) || 0);
      if (el.offsetParent === track && leftScrollbar <= 0) {
        x = el.offsetLeft;
        y = el.offsetTop;
      } else {
        const t = track.getBoundingClientRect();
        const r = el.getBoundingClientRect();
        x = r.left - t.left - track.clientLeft + track.scrollLeft;
        y = r.top - t.top - track.clientTop + track.scrollTop;
        w = r.width;
        h = r.height;
      }
      const prevX = parseFloat(track.style.getPropertyValue('--glass-x')) || x;
      const prevY = parseFloat(track.style.getPropertyValue('--glass-y')) || y;

      track.style.setProperty('--glass-x', `${x}px`);
      track.style.setProperty('--glass-y', `${y}px`);
      track.style.setProperty('--glass-w', `${w}px`);
      track.style.setProperty('--glass-h', `${h}px`);
      track.style.setProperty('--glass-o', '1');

      const moved = Math.abs(x - prevX) + Math.abs(y - prevY);
      if (animate && moved > 2) {
        // Stretch along the axis of travel, then let the spring settle it.
        const horizontal = Math.abs(x - prevX) >= Math.abs(y - prevY);
        track.style.setProperty('--glass-sx', horizontal ? '1.14' : '0.94');
        track.style.setProperty('--glass-sy', horizontal ? '0.88' : '1.1');
        clearTimeout(settle);
        settle = setTimeout(() => {
          track.style.setProperty('--glass-sx', '1');
          track.style.setProperty('--glass-sy', '1');
        }, 170);
      }
    };

    // First placement is instant; transitions switch on afterwards.
    place(false);
    // A timer, not requestAnimationFrame: rAF is paused in background tabs.
    const ready = setTimeout(() => track.classList.add('glass-ready'), 60);

    const ro = new ResizeObserver(() => place(false));
    ro.observe(track);
    Array.from(track.children).forEach((c) => ro.observe(c));

    track.__glassPlace = place;
    return () => {
      clearTimeout(ready);
      clearTimeout(settle);
      ro.disconnect();
    };
  }, [ref, selector]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-measure with animation whenever the selection may have moved.
  useLayoutEffect(() => {
    const track = ref.current;
    if (track?.__glassPlace) track.__glassPlace(true);
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}
